// @ts-nocheck
/**
 * Authentication store using Zustand
 */

import { create } from 'zustand';
import { getCookie } from 'cookies-next';
import { authApi, ImpersonateResponse } from '@/lib/api';
import type { User, LoginCredentials, RegisterData } from '@/types/auth';
import {
  storeToken,
  getToken,
  removeToken,
  verifyTokenSaved,
  getImpersonationData,
  setImpersonationData,
  removeImpersonationData,
  getOriginalAdminToken,
  setOriginalAdminToken,
  removeOriginalAdminToken
} from '@/lib/storageUtils';

// Normalize role to lowercase to keep comparisons consistent with backend uppercase values
const normalizeUserRole = (user: User | null): User | null => {
  if (!user) return user;
  return { ...user, role: (user.role as string)?.toLowerCase() as unknown };
};

interface ImpersonationState {
  isImpersonating: boolean;
  originalUser: User | null;
  impersonatedBy: {
    id: string;
    email: string;
    full_name: string;
  } | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  impersonation: ImpersonationState;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isLoading: false, // Will be set to true by AuthProvider if token exists
  error: null,
  impersonation: {
    isImpersonating: false,
    originalUser: null,
    impersonatedBy: null,
  },

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const authResponse = await authApi.login(credentials);
      const token = authResponse.access_token;

      if (!token) {
        throw new Error('No access token received');
      }

      // Save token with fallback mechanism (iOS Safari/Chrome compatibility)
      storeToken(token);

      // Fetch user data with the token (pass token explicitly to ensure it's used)
      const user = normalizeUserRole(await authApi.getCurrentUser(token));

      // Check if tenant user is trying to login on main domain
      if (user.institution_id && typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        // Main domain: itutor.intrazero.com (3 parts), localhost
        // Tenant subdomain: test.itutor.intrazero.com (4 parts)
        const isMainDomain = parts.length <= 3 || (parts.length === 4 && parts[0] === 'www');

        if (isMainDomain) {
          // Try to get tenant URL from error response if available
          // If not, fetch institution to get subdomain (only if user has institution_id)
          try {
            const institutionRes = await fetch(
              `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:7000'}/v1/institution`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (institutionRes.ok) {
              const institution = await institutionRes.json();
              if (institution.subdomain) {
                const protocol = window.location.protocol;
                // Extract base domain correctly
                // Main domain: itutor.intrazero.com (3 parts) -> base is itutor.intrazero.com
                // Tenant: test.itutor.intrazero.com (4 parts) -> base is itutor.intrazero.com
                const parts = hostname.split('.');
                let baseDomain: string;
                if (parts.length === 3) {
                  // itutor.intrazero.com -> base is itutor.intrazero.com
                  baseDomain = hostname;
                } else if (parts.length > 3) {
                  // test.itutor.intrazero.com -> base is itutor.intrazero.com (skip first part)
                  baseDomain = parts.slice(1).join('.');
                } else if (parts.length === 2) {
                  // intrazero.com -> base is intrazero.com
                  baseDomain = hostname;
                } else {
                  baseDomain = hostname;
                }
                const tenantUrl = `${protocol}//${institution.subdomain}.${baseDomain}`;
                // Redirect to tenant subdomain
                window.location.href = tenantUrl;
                return;
              }
            }
          } catch (err) {
            console.error('Error fetching institution:', err);
          }

          // Clear token and show error
          localStorage.removeItem('token');
          throw new Error('This account belongs to an institution. Please access your account through your institution\'s portal.');
        }
      }

      // Store user_type in localStorage for offline access
      if (typeof window !== 'undefined' && user.user_type) {
        localStorage.setItem('user_type', user.user_type);
      }

      // Check for email verification warning
      if ((authResponse as unknown).warning && !user.email_verified) {
        // Store warning in error field to display to user
        set({
          user,
          token,
          isLoading: false,
          error: (authResponse as unknown).warning,
          impersonation: {
            isImpersonating: false,
            originalUser: null,
            impersonatedBy: null,
          },
        });
        // Don't throw error, allow login but show warning
      } else {
        // Reset impersonation state on new login
        set({
          user,
          token,
          isLoading: false,
          impersonation: {
            isImpersonating: false,
            originalUser: null,
            impersonatedBy: null,
          },
        });
      }
    } catch (error: unknown) {
      // Clear token on error
      removeToken();
      removeImpersonationData();
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      set({
        error: errorMessage,
        isLoading: false,
        user: null,
        token: null,
        impersonation: {
          isImpersonating: false,
          originalUser: null,
          impersonatedBy: null,
        },
      });
      throw error;
    }
  },

  register: async (userData) => {
    set({ isLoading: true, error: null });
    try {
      await authApi.register(userData);

      // Don't auto-login - user needs to verify email first
      // Registration is successful, show success message on page
      set({ isLoading: false });
    } catch (error: unknown) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    removeToken();
    removeImpersonationData();
    removeOriginalAdminToken();
    // Clear user_type from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_type');
    }
    set({
      user: null,
      token: null,
      impersonation: {
        isImpersonating: false,
        originalUser: null,
        impersonatedBy: null,
      },
    });
    await authApi.logout();
  },

  loadUser: async () => {
    // Check if we're on the client side
    if (typeof window === 'undefined') {
      // Server-side: just set loading to false and return
      set({
        user: null,
        token: null,
        isLoading: false,
        impersonation: {
          isImpersonating: false,
          originalUser: null,
          impersonatedBy: null,
        },
      });
      return;
    }

    const token = getToken(); // Use storage utility with fallback
    let activeToken = token;

    // Check for cookie token if storage token is missing
    if (!activeToken) {
      const cookieToken = getCookie('token') as string | undefined;
      if (cookieToken) {
        // Found token in cookie but not in storage - sync it
        activeToken = cookieToken;
        storeToken(activeToken);
      } else {
        // No token anywhere
        set({
          user: null,
          token: null,
          isLoading: false,
          impersonation: {
            isImpersonating: false,
            originalUser: null,
            impersonatedBy: null,
          },
        });
        return;
      }
    }

    // Skip if we're already loading AND we already have a user (prevent duplicate calls)
    // But allow if we're loading but don't have a user yet (initial load)
    const currentState = get();
    if (currentState.isLoading && currentState.user) {
      return;
    }

    set({ isLoading: true });
    try {
      // Add timeout wrapper for mobile Safari compatibility
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout - please check your connection')), 10000)
      );

      const user = normalizeUserRole(await Promise.race([
        authApi.getCurrentUser(activeToken),
        timeoutPromise
      ]) as unknown);

      // Check if impersonating from localStorage
      const impersonationData = localStorage.getItem('impersonating');
      let impersonationState = {
        isImpersonating: false,
        originalUser: null,
        impersonatedBy: null,
      };

      if (impersonationData) {
        try {
          const data = JSON.parse(impersonationData);
          impersonationState = {
            isImpersonating: true,
            originalUser: data.originalUser,
            impersonatedBy: data.impersonatedBy,
          };
        } catch {
          // Invalid impersonation data, clear it
          localStorage.removeItem('impersonating');
        }
      }

      set({ user, token, isLoading: false, impersonation: impersonationState });
    } catch (error: unknown) {
      console.error('Error loading user:', error);
      // Clear invalid token on error
      removeToken();
      removeImpersonationData();
      removeOriginalAdminToken();
      // Clear user_type from localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('user_type');
      }
      set({
        user: null,
        token: null,
        isLoading: false,
        error: error?.message || 'Failed to load user',
        impersonation: {
          isImpersonating: false,
          originalUser: null,
          impersonatedBy: null,
        },
      });
    }
  },

  impersonate: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = get().user;
      const currentToken = get().token;
      if (!currentUser || !currentToken) {
        throw new Error('Must be logged in to impersonate');
      }

      const response: ImpersonateResponse = await authApi.impersonate(userId);
      const token = response.access_token;

      if (!token) {
        throw new Error('No access token received');
      }

      // Store original admin token before replacing it
      setOriginalAdminToken(currentToken);

      // Save token and impersonation data
      storeToken(token);
      setImpersonationData({
        originalUser: currentUser,
        originalToken: currentToken,
        impersonatedBy: response.impersonated_by,
      });

      // Fetch target user
      const targetUser = normalizeUserRole(await authApi.getCurrentUser(token));

      set({
        user: targetUser,
        token,
        isLoading: false,
        impersonation: {
          isImpersonating: true,
          originalUser: currentUser,
          impersonatedBy: response.impersonated_by || null,
        },
      });
    } catch (error: unknown) {
      const errorMessage = error.response?.data?.detail || error.message || 'Impersonation failed';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  stopImpersonating: async () => {
    const originalUser = get().impersonation.originalUser;
    if (!originalUser) {
      return;
    }

    // Get the original admin token from storage
    const originalToken = getOriginalAdminToken();

    if (!originalToken) {
      // Fallback: try to get from impersonating data
      const impersonationData = getImpersonationData();
      if (impersonationData && impersonationData.originalToken) {
        storeToken(impersonationData.originalToken);
        removeOriginalAdminToken();
        removeImpersonationData();

        // Reload user with original token
        set({ isLoading: true });
        try {
          const user = normalizeUserRole(await authApi.getCurrentUser(impersonationData.originalToken));
          set({
            user,
            token: impersonationData.originalToken,
            isLoading: false,
            impersonation: {
              isImpersonating: false,
              originalUser: null,
              impersonatedBy: null,
            },
          });
          window.location.reload();
          return;
        } catch (error) {
          console.error('Failed to restore original user:', error);
          // Fall through to reload
        }
      }

      // If no token found, just clear and reload (user will need to log in again)
      removeImpersonationData();
      removeOriginalAdminToken();
      removeToken();
      set({
        user: null,
        token: null,
        impersonation: {
          isImpersonating: false,
          originalUser: null,
          impersonatedBy: null,
        },
      });
      window.location.href = '/auth/login';
      return;
    }

    // Restore original admin token
    storeToken(originalToken);
    removeImpersonationData();
    removeOriginalAdminToken();

    set({
      impersonation: {
        isImpersonating: false,
        originalUser: null,
        impersonatedBy: null,
      },
    });

    // Reload user with original token
    set({ isLoading: true });
    try {
      const user = await authApi.getCurrentUser(originalToken);
      set({
        user,
        token: originalToken,
        isLoading: false,
      });
      window.location.reload();
    } catch (error) {
      console.error('Failed to restore original user:', error);
      // On error, clear everything and redirect to login
      removeToken();
      removeOriginalAdminToken();
      set({
        user: null,
        token: null,
        isLoading: false,
      });
      window.location.href = '/auth/login';
    }
  },

  clearError: () => set({ error: null }),
}));

